import { JOB_STAGES, STAGE_MAP } from 'constants/jobStage';
import { JobWithMetadata, Job } from 'services/api/job';

export type GroupedJobs = {
  [key: string]: JobWithMetadata[];
};

export const STAGE_ORDER = [
  JOB_STAGES.NEW_JOB,
  JOB_STAGES.JOB_STAGED,
  JOB_STAGES.EQUIPMENT_SHIPPED,
  JOB_STAGES.OPERATOR_DISPATCHED,
  JOB_STAGES.OPERATOR_RETURNED,
  JOB_STAGES.EQUIPMENT_RETURNED
] as const;

/**
 * Calculates the number of days since a job was moved to "Equipment Returned" stage
 */
// export const calculateDaysReturned = (job: Job): number => {
//   if (!job.stageHistory || job.stageHistory.length === 0) {
//     return 0;
//   }

//   const returnedStageHistory = job.stageHistory
//     .filter(
//       history =>
//         history.stage.name.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURNED ||
//         history.stage.name.toLowerCase() === STAGE_MAP['Equipment Returned']
//     )

//     .sort(
//       (a, b) =>
//         new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
//     );

//   if (returnedStageHistory.length === 0) {
//     return 0;
//   }

//   const returnDate = new Date(returnedStageHistory[0].timestamp);
//   const today = new Date();
//   const diffTime = Math.abs(today.getTime() - returnDate.getTime());
//   return Math.floor(diffTime / (1000 * 60 * 60 * 24));
// };

export const calculateDaysReturned = (job: Job): number => {
  if (!job.returnDate) return 0;

  const returnDate = new Date(job.returnDate);
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - returnDate.getTime());

  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Gets the background color class for a job card based on its status
 */
export const getCardColorByDaysReturned = (job: JobWithMetadata): string => {
  const isEquipmentReturned =
    job.jobStage.name.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURNED ||
    job.jobStage.name.toLowerCase() === STAGE_MAP['Equipment Returned'];
  if (!isEquipmentReturned) return '';

  if (job.isPriority) return 'bg-striped-danger';

  const days = job.daysReturned;
  if (days <= 10) return 'bg-success';
  if (days <= 21) return 'bg-warning';

  return 'bg-danger';
};

/**
 * Enriches a job with metadata (days returned and priority status)
 */
export const enrichJobWithMetadata = (job: Job): JobWithMetadata => ({
  ...job,
  daysReturned: calculateDaysReturned(job),
  isPriority: job.isPriority
});

/**
 * Sorts jobs within a specific stage according to stage rules
 */
export const sortJobsByStage = (
  jobs: JobWithMetadata[],
  stageName: string
): JobWithMetadata[] => {
  const stage = stageName.toLowerCase();
  const groupedJobs = [];
  switch (stage) {
    case JOB_STAGES.NEW_JOB:
    case JOB_STAGES.JOB_STAGED: {
      const job = [...jobs].sort(
        (a, b) =>
          new Date(a.expectedEquipmentShipDate).getTime() -
          new Date(b.expectedEquipmentShipDate).getTime()
      );
      groupedJobs.push(job);
      return job;
    }
    case JOB_STAGES.EQUIPMENT_SHIPPED:
    case JOB_STAGES.OPERATOR_DISPATCHED:
    case JOB_STAGES.OPERATOR_RETURNED: {
      const job = [...jobs].sort((a, b) =>
        (a.customer?.organizationName || '').localeCompare(
          b.customer?.organizationName || ''
        )
      );
      groupedJobs.push(job);
      return job;
    }
    case JOB_STAGES.EQUIPMENT_RETURNED:
    case STAGE_MAP['Equipment Returned'].toLowerCase(): {
      const job = [...jobs].sort((a, b) => {
        if (a.isPriority !== b.isPriority) {
          return a.isPriority ? -1 : 1;
        }
        return (b.daysReturned || 0) - (a.daysReturned || 0);
      });
      groupedJobs.push(job);
      return job;
    }

    default: {
      groupedJobs.push(jobs);
      return jobs;
    }
  }
};

/**
 * Groups and sorts jobs according to stage rules
 */
export const groupAndSortJobs = (jobs: JobWithMetadata[]): GroupedJobs => {
  const grouped: GroupedJobs = {};

  // Initialize groups in correct order
  STAGE_ORDER.forEach(stage => {
    grouped[stage] = [];
  });

  // Group jobs by stage
  jobs.forEach(job => {
    const stageName = job.jobStage.name.toLowerCase();
    if (grouped[stageName] !== undefined) {
      grouped[stageName].push(job);
    }
  });

  // Sort each group according to its rules
  STAGE_ORDER.forEach(stage => {
    if (grouped[stage]) {
      grouped[stage] = sortJobsByStage(grouped[stage], stage);
    }
  });

  return grouped;
};

/**
 * Gets the total count of jobs across all stages
 */
export const getTotalJobCount = (groupedJobs: GroupedJobs): number =>
  Object.values(groupedJobs).reduce((total, jobs) => total + jobs.length, 0);

/**
 * Checks if a job is overdue based on its stage and returned days
 */
export const isJobOverdue = (job: JobWithMetadata): boolean => {
  const isEquipmentReturned =
    job.jobStage.name.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURNED ||
    job.jobStage.name.toLowerCase() === STAGE_MAP['Equipment Returned'];
  return isEquipmentReturned && job.daysReturned > 21;
};

/**
 * Gets the current stage index of a job
 */
export const getStageIndex = (stageName: string): number =>
  STAGE_ORDER.findIndex(
    stage => stage.toLowerCase() === stageName.toLowerCase()
  );

/**
 * Validates if a job has all required equipment ship date information
 */
export const hasValidShipmentInfo = (job: Job): boolean =>
  Boolean(job.expectedEquipmentShipDate) &&
  new Date(job.expectedEquipmentShipDate).toString() !== 'Invalid Date';

/**
 * Formats a date string or Date object into a consistent format
 */
export const formatDate = (date: string | Date): string => {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Helper function to safely compare dates
 */
export const compareDates = (
  date1: string | Date,
  date2: string | Date
): number => {
  const d1 = new Date(date1).getTime();
  const d2 = new Date(date2).getTime();
  return d1 - d2;
};

/**
 * Validates if a stage transition is allowed
 */
export const isValidStageTransition = (
  currentStage: string,
  targetStage: string,
  job: Job
): boolean => {
  const currentIndex = getStageIndex(currentStage);
  const targetIndex = getStageIndex(targetStage);

  // Can't move more than one stage at a time
  if (Math.abs(targetIndex - currentIndex) > 1) {
    return false;
  }

  // Check for open tasks if moving to Equipment Returned
  if (
    targetStage.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURNED ||
    STAGE_MAP['Equipment Returned'].toLowerCase()
  ) {
    return !job?.tasks?.some(task => !task.completed);
  }

  return true;
};

/**
 * Gets the CSS classes for the job card
 */
export const getJobCardClasses = (job: JobWithMetadata): string => {
  const baseClasses = 'h-100 hover-actions-trigger';
  const colorClass = getCardColorByDaysReturned(job);
  return `${baseClasses} ${colorClass}`.trim();
};

/**
 * Gets the background color class for a job card based on Equipment Returned status
 */
export const getCardStyleForReturnedEquipment = (
  job: JobWithMetadata
): string => {
  const isEquipmentReturned =
    job.jobStage.name.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURNED ||
    job.jobStage.name.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURN;

  const isOperatorDispatched =
    job.jobStage.name.toLowerCase() === JOB_STAGES.OPERATOR_DISPATCHED ||
    job.jobStage.name.toLowerCase() === STAGE_MAP['Operator Dispatched'];

  const isEquipmentShipped =
    job.jobStage.name.toLowerCase() === JOB_STAGES.EQUIPMENT_SHIPPED ||
    job.jobStage.name.toLowerCase() === STAGE_MAP['Equipment Shipped'];

  // Handle Equipment Shipped with priority
  if (isEquipmentShipped && job.isPriority) {
    return 'bg-danger-subtle';
  }

  // Handle Operator Dispatched with priority
  if (isOperatorDispatched && job.isPriority) {
    return 'bg-danger-subtle';
  }

  // Handle Equipment Returned status
  if (isEquipmentReturned) {
    if (job.isPriority) return 'bg-danger-subtle';

    if (job.daysReturned <= 10) return 'bg-success-subtle';

    if (job.daysReturned <= 21) return 'bg-warning-subtle';

    return 'bg-danger-subtle';
  }

  return '';
};

// Add necessary CSS for the striped background
export const addStripedBackgroundStyles = () => {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    .bg-striped-danger {
      background-color: #dc3545 !important;
      color: #fff !important;
    }
  .bg-success-subtle {
  border: green solid 2px !important;
}

.bg-warning-subtle {
  border: orange solid 2px !important;
}

.bg-danger-subtle {
  border: red solid 2px !important;
}
  `;
  document.head.appendChild(styleSheet);
};
